import React, { useState } from 'react';
import { X, Building2, User, MessageSquare, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { contactService, type ContactFormData } from '@/services/contactService';
import { logError } from '@/utils/errorHandler';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPurpose?: string;
}

type FormData = ContactFormData;

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  companySize?: string;
  purpose?: string;
  currentSolution?: string;
  message?: string;
}

export function ContactModal({ isOpen, onClose, initialPurpose }: ContactModalProps) {
  const {
    getContactModal,
    getContactSection,
    getContactField,
    getContactButton,
    getContactSuccess,
    getCompanySizeOptions,
    getPurposeOptions,
  } = useMarketingTranslations();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    purpose: initialPurpose || '',
    currentSolution: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Update form data when initialPurpose changes
  React.useEffect(() => {
    if (initialPurpose) {
      setFormData(prev => ({ ...prev, purpose: initialPurpose }));
    }
  }, [initialPurpose]);

  // Get translated options for dropdowns, but send English values to backend
  const companySizeOptions = getCompanySizeOptions();
  const purposeOptions = getPurposeOptions();

  // Define the mapping between translated display text and backend values
  const companySizeMapping: Record<string, string> = {
    // English (no mapping needed)
    '1-10 employees': '1-10 employees',
    '11-50 employees': '11-50 employees',
    '51-200 employees': '51-200 employees',
    '201-500 employees': '201-500 employees',
    '501-1000 employees': '501-1000 employees',
    '1000+ employees': '1000+ employees',
    // Spanish
    '1-10 empleados': '1-10 employees',
    '11-50 empleados': '11-50 employees',
    '51-200 empleados': '51-200 employees',
    '201-500 empleados': '201-500 employees',
    '501-1000 empleados': '501-1000 employees',
    'Más de 1000 empleados': '1000+ employees',
    // Portuguese
    '1-10 funcionários': '1-10 employees',
    '11-50 funcionários': '11-50 employees',
    '51-200 funcionários': '51-200 employees',
    '201-500 funcionários': '201-500 employees',
    '501-1000 funcionários': '501-1000 employees',
    'Mais de 1000 funcionários': '1000+ employees',
    // German
    '1-10 Mitarbeiter': '1-10 employees',
    '11-50 Mitarbeiter': '11-50 employees',
    '51-200 Mitarbeiter': '51-200 employees',
    '201-500 Mitarbeiter': '201-500 employees',
    '501-1000 Mitarbeiter': '501-1000 employees',
    'Über 1000 Mitarbeiter': '1000+ employees',
  };

  const purposeMapping: Record<string, string> = {
    // English (no mapping needed)
    'Request a Demo': 'Request a Demo',
    'Start a Free Trial': 'Start a Free Trial',
    'Request a Free Trial': 'Request a Free Trial',
    'Speak with Sales': 'Speak with Sales',
    'Get More Information': 'Get More Information',
    // Spanish
    'Solicitar una demo': 'Request a Demo',
    'Comenzar una prueba gratuita': 'Start a Free Trial',
    'Solicitar una prueba gratuita': 'Request a Free Trial',
    'Hablar con ventas': 'Speak with Sales',
    'Obtener más información': 'Get More Information',
    // Portuguese
    'Solicitar uma demonstração': 'Request a Demo',
    'Iniciar teste gratuito': 'Start a Free Trial',
    'Solicitar teste gratuito': 'Request a Free Trial',
    'Falar com vendas': 'Speak with Sales',
    'Obter mais informações': 'Get More Information',
    // German
    'Demo anfordern': 'Request a Demo',
    'Kostenlose Testversion starten': 'Start a Free Trial',
    'Kostenlose Testversion anfordern': 'Request a Free Trial',
    'Mit Vertrieb sprechen': 'Speak with Sales',
    'Weitere Informationen erhalten': 'Get More Information',
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = getContactField('firstName', 'error');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = getContactField('lastName', 'error');
    }
    if (!formData.email.trim()) {
      newErrors.email = getContactField('email', 'errorRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = getContactField('email', 'errorInvalid');
    }
    if (!formData.company.trim()) {
      newErrors.company = getContactField('company', 'error');
    }
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = getContactField('jobTitle', 'error');
    }
    if (!formData.companySize.trim()) {
      newErrors.companySize = getContactField('companySize', 'error');
    }
    if (!formData.purpose.trim()) {
      newErrors.purpose = getContactField('purpose', 'error');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Map translated values to English values for backend
    const backendFormData = {
      ...formData,
      companySize: companySizeMapping[formData.companySize] || formData.companySize,
      purpose: purposeMapping[formData.purpose] || formData.purpose,
    };

    try {
      const result = await contactService.submitContactForm(backendFormData);

      if (result.success) {
        setIsSubmitted(true);

        setTimeout(() => {
          handleClose();
        }, 5000);
      } else {
        logError(new Error(`Contact form submission failed: ${result.message}`), {
          component: 'ContactModal',
          action: 'handleSubmit',
          metadata: {
            resultMessage: result.message,
            formData: backendFormData,
            context: 'form_submission_failed',
          },
        });
      }
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ContactModal',
        action: 'handleSubmit',
        metadata: {
          formData: backendFormData,
          context: 'form_submission_error',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      companySize: '',
      purpose: initialPurpose || '',
      currentSolution: '',
      message: '',
    });
    setErrors({});
    setIsSubmitted(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        />

        <motion.div
          className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/95 to-slate-900/95 shadow-2xl backdrop-blur-sm"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex items-center justify-between border-b border-slate-700/50 p-6">
            <div>
              <h2 className="text-2xl font-bold text-white">{getContactModal('title')}</h2>
              <p className="mt-1 text-slate-300">{getContactModal('subtitle')}</p>
            </div>
            <button
              onClick={handleClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-700/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={getContactModal('close')}
            >
              <X className="size-6" />
            </button>
          </div>

          <div className="max-h-[calc(90vh-160px)] overflow-y-auto p-6">
            {isSubmitted ? (
              <motion.div
                className="py-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/20">
                  <svg
                    className="size-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-white">
                  {getContactSuccess('title')}
                </h3>
                <p className="mb-4 text-slate-300">{getContactSuccess('message')}</p>
                <p className="text-sm text-slate-400">{getContactSuccess('closing')}</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                    <User className="mr-2 size-5 text-primary-400" />
                    {getContactSection('personal')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('firstName', 'label')} *
                      </label>
                      <Input
                        id="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={e => handleInputChange('firstName', e.target.value)}
                        className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errors.firstName ? 'border-red-500' : ''
                        }`}
                        placeholder={getContactField('firstName', 'label')}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('lastName', 'label')} *
                      </label>
                      <Input
                        id="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={e => handleInputChange('lastName', e.target.value)}
                        className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errors.lastName ? 'border-red-500' : ''
                        }`}
                        placeholder={getContactField('lastName', 'label')}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('email', 'label')} *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={e => handleInputChange('email', e.target.value)}
                        className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                        placeholder={getContactField('email', 'label')}
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('phone', 'label')}
                      </label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={e => handleInputChange('phone', e.target.value)}
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500"
                        placeholder={getContactField('phone', 'label')}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                    <Building2 className="mr-2 size-5 text-primary-400" />
                    {getContactSection('company')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="company"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('company', 'label')} *
                      </label>
                      <Input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={e => handleInputChange('company', e.target.value)}
                        className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errors.company ? 'border-red-500' : ''
                        }`}
                        placeholder={getContactField('company', 'label')}
                      />
                      {errors.company && (
                        <p className="mt-1 text-sm text-red-400">{errors.company}</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="jobTitle"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('jobTitle', 'label')} *
                      </label>
                      <Input
                        id="jobTitle"
                        type="text"
                        value={formData.jobTitle}
                        onChange={e => handleInputChange('jobTitle', e.target.value)}
                        className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errors.jobTitle ? 'border-red-500' : ''
                        }`}
                        placeholder={getContactField('jobTitle', 'label')}
                      />
                      {errors.jobTitle && (
                        <p className="mt-1 text-sm text-red-400">{errors.jobTitle}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label
                      htmlFor="companySize"
                      className="mb-2 block text-sm font-medium text-slate-300"
                    >
                      {getContactField('companySize', 'label')} *
                    </label>
                    <div className="relative">
                      <select
                        id="companySize"
                        value={formData.companySize}
                        onChange={e => handleInputChange('companySize', e.target.value)}
                        className={`w-full appearance-none rounded-lg border border-slate-600 bg-slate-700/50 p-3 text-white placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-primary-500 ${
                          errors.companySize ? 'border-red-500' : ''
                        }`}
                      >
                        <option value="">{getContactField('companySize', 'label')}</option>
                        {companySizeOptions.map((size: string) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
                    </div>
                    {errors.companySize && (
                      <p className="mt-1 text-sm text-red-400">{errors.companySize}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
                    <MessageSquare className="mr-2 size-5 text-primary-400" />
                    {getContactSection('details')}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="purpose"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('purpose', 'label')} *
                      </label>
                      <div className="relative">
                        <select
                          id="purpose"
                          value={formData.purpose}
                          onChange={e => handleInputChange('purpose', e.target.value)}
                          className={`w-full appearance-none rounded-lg border border-slate-600 bg-slate-700/50 p-3 text-white placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-primary-500 ${
                            errors.purpose ? 'border-red-500' : ''
                          }`}
                        >
                          <option value="">{getContactField('purpose', 'label')}</option>
                          {purposeOptions.map((purpose: string) => (
                            <option key={purpose} value={purpose}>
                              {purpose}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
                      </div>
                      {errors.purpose && (
                        <p className="mt-1 text-sm text-red-400">{errors.purpose}</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="currentSolution"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('currentSolution', 'label')}
                      </label>
                      <Input
                        id="currentSolution"
                        type="text"
                        value={formData.currentSolution}
                        onChange={e => handleInputChange('currentSolution', e.target.value)}
                        className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500"
                        placeholder={getContactField('currentSolution', 'placeholder')}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="message"
                        className="mb-2 block text-sm font-medium text-slate-300"
                      >
                        {getContactField('message', 'label')}
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        value={formData.message}
                        onChange={e => handleInputChange('message', e.target.value)}
                        className="w-full resize-none rounded-lg border border-slate-600 bg-slate-700/50 p-3 text-white placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                        placeholder={getContactField('message', 'placeholder')}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-3 font-semibold text-white transition-all duration-200 hover:from-primary-700 hover:to-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? getContactButton('submitting') : getContactButton('submit')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
